import { Head, usePage } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";

import AppLogoIcon from "@/components/app-logo-icon";
interface WaitingProps {
  session_id: string;
  url: string;
  social_image: {
    id: number;
    status: string;
    title?: string;
    description?: string;
    customer_email?: string | null;
    style?: string;
  };
}

interface StatusResponse {
  status: string;
  error?: string;
}

export default function Waiting({ session_id, url, social_image }: WaitingProps) {
  const { auth } = usePage().props;
  const [error, setError] = useState<string | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [status, setStatus] = useState<string>(social_image?.status || "pending");

  const maxPollTime = 5 * 60 * 1000; // 5 minutes
  const pollInterval = 5000; // 5 seconds
  const maxPolls = maxPollTime / pollInterval;

  const handleRetry = () => {
    setError(null);
    setIsTimedOut(false);
    window.location.reload();
  };

  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const response = await fetch(`/generation/status?session_id=${session_id}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = (await response.json()) as StatusResponse;
        setStatus(data.status);

        if (data.status === "completed") {
          window.location.reload();
          clearInterval(poll);
        } else if (data.status === "failed") {
          setError(data.error ?? "Generation failed. Please try again.");
          clearInterval(poll);
        }
      } catch (error) {
        console.error("Polling error:", error);
        setError(error instanceof Error ? `Connection error: ${error.message}` : "Unable to check generation status");
        clearInterval(poll);
      }
    }, pollInterval);

    return () => clearInterval(poll);
  }, [session_id, url, maxPolls, pollInterval]);

  // Collapse backend statuses into 3 user-friendly phases
  const groups: { label: string; keys: string[] }[] = [
    {
      label: "Analyzing your page",
      keys: ["pending", "processing", "screenshot", "metadata", "analyzing"],
    },
    {
      label: "Creating designs",
      keys: ["generating"],
    },
    { label: "Finalizing images", keys: ["finalizing"] },
  ] as const;

  const groupIndexForStatus = (s: string) => {
    if (s === "completed") return groups.length;
    const idx = groups.findIndex((g) => g.keys.includes(s));
    return idx === -1 ? 0 : idx;
  };

  const currentIndex = groupIndexForStatus(status);

  return (
    <>
      <Head title="Generating Your Social Previews" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto max-w-4xl px-4 py-20">
          <div className="text-center">
            <div className="mb-8">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-orange-500">
                <AppLogoIcon className="h-8 w-8 text-white" />
              </div>
              <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
                {error || isTimedOut ? "Generation Issue" : "Generating Your Social Previews"}
              </h1>
              <p className="mb-8 text-xl text-gray-600 dark:text-gray-300">
                {error
                  ? "Something went wrong during generation."
                  : isTimedOut
                    ? "Generation is taking longer than expected."
                    : "This usually takes 30-60 seconds"}
              </p>
            </div>

            <div className="mx-auto max-w-md">
              <div className="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
                {error || isTimedOut ? (
                  <div className="mb-6 flex items-center justify-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                      <span className="text-2xl text-red-600 dark:text-red-400">⚠</span>
                    </div>
                  </div>
                ) : null}

                <div>
                  {groups.map((step, idx) => {
                    const isCompleted = currentIndex > idx;
                    const isCurrent = currentIndex === idx;
                    return (
                      <div key={step.label} className="flex items-start space-x-3">
                        <div
                          className={`mt-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${isCompleted ? "bg-green-100 dark:bg-green-900" : isCurrent ? "bg-orange-100 dark:bg-orange-900" : "bg-gray-200 dark:bg-gray-700"}`}
                        >
                          {isCompleted ? (
                            <span className="text-sm font-semibold text-green-700 dark:text-green-300">✓</span>
                          ) : isCurrent ? (
                            <LoaderCircle className="h-4 w-4 animate-spin text-orange-600 dark:text-orange-400" />
                          ) : (
                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">{idx + 1}</span>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col justify-center gap-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{step.label}</p>
                          <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                              className={`h-2 rounded-full ${isCompleted ? "bg-green-500" : isCurrent ? "animate-pulse bg-orange-500" : "bg-gray-300 dark:bg-gray-600"}`}
                              style={{
                                width: isCompleted ? "100%" : isCurrent ? "60%" : "0%",
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {error || isTimedOut ? (
                  <div className="mt-6 space-y-4">
                    {error && (
                      <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                      </div>
                    )}
                    {isTimedOut && (
                      <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
                        <p className="text-sm text-yellow-700 dark:text-yellow-300">
                          The generation is taking longer than usual. This might be due to high demand or a complex
                          website.
                        </p>
                      </div>
                    )}
                    <button
                      onClick={handleRetry}
                      className="w-full rounded-lg bg-orange-500 px-4 py-2 text-white hover:bg-orange-600 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:outline-none dark:focus:ring-offset-gray-800"
                    >
                      Try Again
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      If the problem persists, please{" "}
                      <a href="mailto:support@ogpilot.com" target="_blank" rel="noopener noreferrer">
                        contact support
                      </a>
                      .
                    </p>
                  </div>
                ) : (
                  <p className="mt-6 text-xs text-balance text-gray-500 dark:text-gray-400">
                    {auth?.user || social_image.customer_email ? (
                      <>
                        No need to keep this page open.
                        <br />
                        We&apos;ll email you at <strong>{auth?.user?.email || social_image.customer_email}</strong> when
                        the images are ready.
                      </>
                    ) : (
                      "Please keep this page open while we generate your images."
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
